import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button, F0Card, F0TagStatus } from '@factorialco/f0-react'
import { ChevronDown, ChevronUp } from '@factorialco/f0-react/icons/app'
import {
  ContractHistoryActivityPeriodFragment,
  ContractHistoryContractVersionHistoryFragment,
  ContractHistoryContractVersionUpdateFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import i18n from 'modules/core/lib/i18n'

import { getContractVersionTitle } from 'modules/contracts/lib/ContractHistoryUtils'
import { useRoute } from 'modules/contracts/pages/ContractVersionsPage/_RouteContext'

import CardContent from './CardContent'

interface Props {
  activityPeriod: ContractHistoryActivityPeriodFragment
  contractVersionUpdates: ContractHistoryContractVersionUpdateFragment[]
  version: ContractHistoryContractVersionHistoryFragment
  firstContractVersion: boolean
  firstContract: boolean
  onContractVersionSelect?: (referenceVersionId: number | undefined) => void
  selectedContractVersionId: number | undefined
  contractOngoingActivityPeriod?: ContractHistoryActivityPeriodFragment | undefined
  isSingleUpcomingActivityPeriod: boolean
}

const ContractVersionCard = ({
  activityPeriod,
  contractVersionUpdates,
  version,
  firstContractVersion,
  firstContract,
  onContractVersionSelect,
  selectedContractVersionId,
  contractOngoingActivityPeriod,
  isSingleUpcomingActivityPeriod,
}: Props) => {
  const route = useRoute()
  const isOngoing = activityPeriod.schedule === 'ongoing'
  const isUpcoming = activityPeriod.schedule === 'upcoming'
  const isSelected = selectedContractVersionId
    ? activityPeriod.contractVersionIds.includes(selectedContractVersionId) && !isOngoing
    : false
  const [open, setOpen] = useState(isOngoing || isSelected)
  const showCardAction =
    open &&
    !isOngoing &&
    !!contractOngoingActivityPeriod &&
    contractOngoingActivityPeriod.referenceVersionId !== version.id

  const actionLabel = isSelected
    ? i18n.t('contract_versions.navigator.history.back_to_current_conditions')
    : isUpcoming
    ? i18n.t('contract_versions.navigator.history.see_future_conditions')
    : i18n.t('contract_versions.navigator.history.see_old_conditions')

  const onContractVersionSelected = () => {
    const contractVersion = isSelected
      ? contractOngoingActivityPeriod || activityPeriod
      : activityPeriod

    window.history.replaceState(
      null,
      'Factorial',
      route.contractVersionPath(contractVersion.referenceVersionId)
    )
    onContractVersionSelect?.(contractVersion.referenceVersionId)
  }

  return (
    <Box flexDirection="column" gap="s8" width="full">
      <Box position="relative" overflowX="hidden">
        {isSelected && (
          <Box
            position="absolute"
            borderRadius={{ all: 'abs012' }}
            border={{
              all: {
                color: 'viridian800',
                width: 's1',
                style: 'solid',
              },
            }}
            style={{
              background: 'rgba(7, 162, 173, 0.05)',
              inset: '0',
              zIndex: '2',
              pointerEvents: 'none',
            }}
          />
        )}
        <Box position="relative">
          <F0Card
            title={getContractVersionTitle({
              actionType: version.actionType || '',
              firstContractVersion: firstContractVersion,
              firstContract: firstContract,
              isSingleUpcomingActivityPeriod,
            })}
            description={moment(version.effectiveOn).format('MMM Do YYYY')}
            {...(showCardAction && {
              secondaryActions: [
                {
                  label: actionLabel,
                  onClick: onContractVersionSelected,
                },
              ],
            })}
          >
            {open && (
              <CardContent
                contractVersion={version}
                contractVersionUpdates={contractVersionUpdates}
              />
            )}
          </F0Card>
        </Box>
        <Box
          position="absolute"
          style={{ top: '16px', right: '16px' }}
          flexDirection="row"
          gap="s8"
        >
          {isOngoing && (
            <F0TagStatus
              text={i18n.t('contract_versions.navigator.history.status_tag.ongoing')}
              variant={'positive'}
            />
          )}
          {isUpcoming && (
            <F0TagStatus
              text={i18n.t('contract_versions.navigator.history.status_tag.upcoming')}
              variant={'warning'}
            />
          )}
          {!isOngoing && (
            <F0Button
              label="Toggle"
              hideLabel
              icon={open ? ChevronUp : ChevronDown}
              size="sm"
              variant="outline"
              onClick={() => setOpen(!open)}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default ContractVersionCard
